import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import Page from 'commons/page'
import Block from 'components/block'
import { useAuthProvider } from 'contexts/auth'
import { useAppDispatch, useAppSelector } from 'hooks'
import AccessDenied from 'pages/access-denied'
import {
  fetchContractById,
  selectContractById,
  selectContractByIdFailed,
  selectContractByIdLoading,
} from 'stores/contract'

import SubPageHeader from '../../components/sub-page-header'
import SuccessfullyConcludedDealList from '../../features/contracts/list/successfully-concluded-deal-list'
import { SuccessfullyConcludedDealItem } from '../../schema/contract'
import PropertyMapView from '../../components/property-map-view'

const SuccessfullyConcludedDealsMap = () => {
  const { claims } = useAuthProvider()
  const contract = useAppSelector(selectContractById)
  const contractLoading = useAppSelector(selectContractByIdLoading)
  const contractFailed = useAppSelector(selectContractByIdFailed)
  const dispatch = useAppDispatch()
  const { id } = useParams<{ id: string }>()

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const [currentPanelIndex, setCurrentPanelIndex] = useState<number>(0)

  useEffect(() => {
    if (!claims?.role) {
      return
    }

    if (!id) {
      return
    }

    dispatch(fetchContractById({ role: claims?.role, id }))
  }, [dispatch, claims?.role, id])

  const data = useMemo(() => {
    return contract
  }, [contract])

  const HasApiError = useMemo(() => {
    return contractFailed
  }, [contractFailed])

  const assignNumbers = (items: SuccessfullyConcludedDealItem[]) => {
    let currentItemNumber = 1
    return items.map((item) => {
      const isLocated = item.lat && item.lon
      const itemNumberLabel = isLocated ? String(currentItemNumber++) : '-'
      return {
        ...item,
        itemNumberLabel,
      }
    })
  }

  const { successfullyConcludedDeals, position, displayConfig } =
    useMemo(() => {
      if (!data) {
        return {
          position: null,
          successfullyConcludedDeals: [],
          displayConfig: null,
        }
      }
      const numberedSuccessfullyConcludedDeals = assignNumbers(
        (data.successfullyConcludedDeal?.items ?? []).filter(
          (item) => item.lat && item.lon
        )
      )

      const config = {
        address: data.successfullyConcludedDeal?.config.address,
        square: data.successfullyConcludedDeal?.config.square,
        buildYearMonth: data.successfullyConcludedDeal?.config.buildYearMonth,
      }

      return {
        position: {
          lat: Number(data.successfullyConcludedDeal?.config.lat) ?? null,
          lon: Number(data.successfullyConcludedDeal?.config.lon) ?? null,
        },
        successfullyConcludedDeals: numberedSuccessfullyConcludedDeals,
        displayConfig: config,
      }
    }, [data])

  const visibleDeals = useMemo(() => {
    return successfullyConcludedDeals.filter((item) => item.lat && item.lon)
  }, [successfullyConcludedDeals])

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1200)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => {
      window.removeEventListener('resize', checkMobile)
    }
  }, [])

  const handleItemClick = (item: SuccessfullyConcludedDealItem) => {
    if (!item.lat || !item.lon) {
      return
    }

    const itemIndex = successfullyConcludedDeals.findIndex(
      (i) => i.id === item.id
    )
    if (itemIndex !== -1) {
      setSelectedItemId(`marker-${itemIndex}`)
      // モバイルの場合はパネルインデックスも更新
      if (isMobile) {
        const visibleIndex = visibleDeals.findIndex((i) => i.id === item.id)
        if (visibleIndex !== -1) {
          setCurrentPanelIndex(visibleIndex)
        }
      }
    }
  }

  const currentVisibleItem =
    useMemo((): SuccessfullyConcludedDealItem | null => {
      if (!isMobile || visibleDeals.length === 0) return null
      return visibleDeals[currentPanelIndex] || null
    }, [isMobile, visibleDeals, currentPanelIndex])

  return (
    <>
      {HasApiError && <AccessDenied />}
      {!HasApiError && (
        <Page
          title={`周辺の成約事例 - 地図`}
          loading={contractLoading}
          type="user"
          infiniteScroll
          customHeader={<SubPageHeader />}
        >
          {successfullyConcludedDeals.length !== 0 && (
            <Block>
              <PropertyMapView
                pageTitle="周辺の成約事例 - 地図"
                sectionTitle="周辺の成約事例"
                items={successfullyConcludedDeals}
                position={position}
                displayConfig={displayConfig}
                visibleItems={visibleDeals}
                currentVisibleItem={currentVisibleItem}
                selectedItemId={selectedItemId || undefined}
                isMobile={isMobile}
                onItemClick={handleItemClick}
                currentPanelIndex={currentPanelIndex}
                onPanelIndexChange={setCurrentPanelIndex}
                renderList={({
                  items,
                  currentVisibleItem,
                  isMobile,
                  selectedItemId,
                  onItemClick,
                }) => (
                  <SuccessfullyConcludedDealList
                    list={items}
                    isVertical={true}
                    background="white"
                    onItemClick={onItemClick}
                    currentVisibleItem={currentVisibleItem}
                    isMobile={isMobile}
                    selectedItemId={selectedItemId}
                    noSelectedMode={isMobile}
                  />
                )}
                getAccordionTitle={(hasConditions) =>
                  hasConditions
                    ? '売出物件と成約事例の表示条件'
                    : '表示についての注意'
                }
              />
            </Block>
          )}
        </Page>
      )}
    </>
  )
}

export default SuccessfullyConcludedDealsMap
